import { Box, TextField, Tooltip, Typography } from '@mui/material';
import { useIntl } from 'react-intl';
import ColorSwatchPicker from '~/components/Inputs/Color/ColorSwatchPicker';
import { DicomColor } from '~/domain/dicom/dicomColor';
import { DENSITY_OVERRIDE_DECIMALS } from '~/utils/units';

interface CouchStructureRowProps {
    /** The library component's own name, shown as the row's fixed label
     *  and used in its accessible names. Deliberately untranslated,
     *  matching the couch library's vendor-data convention. */
    roiName: string;
    color: DicomColor;
    onColorChange: (color: DicomColor) => void;
    name: string;
    onNameChange: (name: string) => void;
    /** Validation message for `name`, or null when it is valid. Shown
     *  under the field: the row that is wrong is the row that says so,
     *  rather than a disabled Apply with no explanation. */
    nameError: string | null;
    /** `CouchComponent.density.relElectron` for this row's component,
     *  shown under the same `ρe` label and the same decimal count as the
     *  cursor's density readout — the two show the same quantity, and a
     *  bare `ρ` at a different precision reads as a different one.
     *  `relMass` is not shown separately, since the library carries the
     *  same number in both fields (see couchLibrary.ts's doc comment).
     *  Read-only: these are effective densities fitted per geometry, not
     *  a per-site clinical override, so editing them here is deliberately
     *  out of scope (unlike the structure list's density override field,
     *  which edits a real ROI Physical Property). */
    relativeDensity: number;
    /** Stamped on both the swatch and the text field, suffixed
     *  `-color` / `-name`. The density readout is stamped `-density`. */
    dataTestId: string;
}

/**
 * One row of the couch model's structure list: a colour swatch and the
 * editable output name, both supplied by the caller and defaulted there
 * from the library component.
 */
export default function CouchStructureRow({
    roiName,
    color,
    onColorChange,
    name,
    onNameChange,
    nameError,
    relativeDensity,
    dataTestId,
}: CouchStructureRowProps) {
    const intl = useIntl();

    return (
        <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1 }}>
            <ColorSwatchPicker
                color={color}
                onChange={onColorChange}
                ariaLabel={intl.formatMessage(
                    { id: 'couch.structureColorAria' },
                    { name: roiName }
                )}
                dataTestId={`${dataTestId}-color`}
            />
            {/* The component's fixed library identity, kept visible next
                to the editable output name: each component carries its
                own density override, and once the names are edited
                nothing else on screen would tie a row back to the
                component it writes. */}
            <Typography
                fontSize={10}
                color="text.secondary"
                sx={{ minWidth: 68, mt: '9px', flexShrink: 0 }}
                data-testid={`${dataTestId}-component`}
            >
                {roiName}
            </Typography>
            {/* Read-only: Apply writes this into REL_ELEC_DENSITY, and a
                TPS computes dose through it, so the value must be
                verifiable before Apply per the house safety clause — but
                these are effective densities fitted per geometry, not a
                clinical override, so per-site curation stays a deferred
                decision and the field is deliberately not editable here
                (contrast the structure list's density OVERRIDE field). */}
            <Tooltip
                title={intl.formatMessage({
                    id: 'couch.structureDensityTooltip',
                })}
            >
                <Typography
                    variant="monoMeta"
                    sx={{
                        minWidth: 60,
                        mt: '9px',
                        flexShrink: 0,
                        fontVariantNumeric: 'tabular-nums',
                    }}
                    data-testid={`${dataTestId}-density`}
                >
                    {`${intl.formatMessage({
                        id: 'densityReadout.electron',
                    })} ${relativeDensity.toFixed(DENSITY_OVERRIDE_DECIMALS)}`}
                </Typography>
            </Tooltip>
            <TextField
                value={name}
                onChange={(e) => onNameChange(e.target.value)}
                aria-label={intl.formatMessage(
                    { id: 'couch.structureNameAria' },
                    { name: roiName }
                )}
                size="small"
                fullWidth
                error={nameError !== null}
                helperText={nameError ?? undefined}
                FormHelperTextProps={{ sx: { fontSize: 10, mx: 0 } }}
                inputProps={{
                    style: { fontSize: 12 },
                    'data-testid': `${dataTestId}-name`,
                }}
            />
        </Box>
    );
}
